interface ConsoleLogData {
  level: 'log' | 'info' | 'warn' | 'error' | 'debug';
  message: string;
  args: any[];
  timestamp: string;
  url?: string;
  userAgent?: string;
  userId?: string;
  deviceInfo?: {
    platform?: string;
    version?: string;
    model?: string;
  };
}

class ConsoleInterceptor {
  private originalConsole: {
    log: typeof console.log;
    info: typeof console.info;
    warn: typeof console.warn;
    error: typeof console.error;
    debug: typeof console.debug;
  };
  private isEnabled: boolean = true;
  private userId?: string;
  private deviceInfo: ConsoleLogData['deviceInfo'];
  private logLevel: 'debug' | 'info' | 'warn' | 'error' = 'info';

  constructor() {
    this.deviceInfo = this.getDeviceInfoFromUserAgent();

    // Set log level from environment variable
    const envLogLevel = process.env.NEXT_PUBLIC_LOG_LEVEL || 'info';
    this.logLevel = this.parseLogLevel(envLogLevel);

    // Store original console methods
    this.originalConsole = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error,
      debug: console.debug,
    };

    this.interceptConsole();
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  setLogLevel(level: string) {
    this.logLevel = this.parseLogLevel(level);
  }

  private parseLogLevel(level: string): 'debug' | 'info' | 'warn' | 'error' {
    switch (level.toLowerCase()) {
      case 'debug':
        return 'debug';
      case 'info':
        return 'info';
      case 'warn':
        return 'warn';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  }

  private shouldLog(level: 'log' | 'info' | 'warn' | 'error' | 'debug'): boolean {
    const levelPriority = {
      debug: 0,
      info: 1,
      log: 1,
      warn: 2,
      error: 3,
    };

    const currentPriority = levelPriority[this.logLevel];
    const messagePriority = levelPriority[level];

    return messagePriority >= currentPriority;
  }

  private getDeviceInfoFromUserAgent() {
    if (typeof window === 'undefined') {
      return {
        platform: 'web',
        version: 'Unknown',
        model: 'Server',
      };
    }

    const userAgent = window.navigator.userAgent;
    let platform = 'web';
    let version = 'Unknown';
    let model = 'Desktop';

    // Detect platform
    if (userAgent.includes('iPhone')) {
      platform = 'iOS';
      model = 'iPhone';
      const match = userAgent.match(/OS (\d+_\d+)/);
      if (match) version = match[1].replace('_', '.');
    } else if (userAgent.includes('iPad')) {
      platform = 'iOS';
      model = 'iPad';
      const match = userAgent.match(/OS (\d+_\d+)/);
      if (match) version = match[1].replace('_', '.');
    } else if (userAgent.includes('Android')) {
      platform = 'Android';
      model = 'Mobile';
      const match = userAgent.match(/Android (\d+\.?\d*)/);
      if (match) version = match[1];
    } else if (userAgent.includes('Windows')) {
      platform = 'Windows';
      if (userAgent.includes('Windows NT 10.0')) version = '10';
      else if (userAgent.includes('Windows NT 6.3')) version = '8.1';
      else if (userAgent.includes('Windows NT 6.2')) version = '8';
      else if (userAgent.includes('Windows NT 6.1')) version = '7';
    } else if (userAgent.includes('Mac')) {
      platform = 'macOS';
      const match = userAgent.match(/Mac OS X (\d+[_\.]\d+)/);
      if (match) version = match[1].replace('_', '.');
    } else if (userAgent.includes('Linux')) {
      platform = 'Linux';
    }

    return { platform, version, model };
  }

  private getDeviceInfo() {
    return {
      ...this.deviceInfo,
      userAgent:
        typeof window !== 'undefined' && window.navigator ? window.navigator.userAgent : 'Unknown',
    };
  }

  private async sendToServer(logData: ConsoleLogData) {
    if (!this.isEnabled) return;

    // DISABLED: Console logging to server is temporarily disabled
    // // Check if this log level should be sent based on LOG_LEVEL
    // if (!this.shouldLog(logData.level)) return;

    // // Prevent logging errors about logging failures to avoid infinite loops
    // if (
    //   logData.message.includes('Failed to log info to server') ||
    //   logData.message.includes('Failed to send console log to server') ||
    //   (logData.message.includes('Request failed with status code 404') &&
    //     logData.message.includes('/error-logging/'))
    // ) {
    //   return;
    // }

    // try {
    //   // Map console.log to info level since errorLogger doesn't support 'log'
    //   const level = logData.level === 'log' ? 'info' : logData.level;

    //   // Use the existing error logger's logInfo method
    //   await errorLogger.logInfo(level, `Browser: ${logData.message}`, {
    //     args: logData.args,
    //     url: logData.url,
    //     userAgent: logData.userAgent,
    //     deviceInfo: logData.deviceInfo,
    //     consoleLevel: logData.level,
    //   });
    // } catch (error) {
    //   // Silently fail - don't want console interception to crash the app
    //   // Don't use console.warn here to avoid interception loop
    // }
  }

  private formatMessage(args: any[]): string {
    return args
      .map((arg) => {
        if (typeof arg === 'object') {
          try {
            // Limit object size to prevent large payloads
            const stringified = JSON.stringify(arg);
            return stringified.length > 500 ? stringified.substring(0, 500) + '...' : stringified;
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(' ');
  }

  private sanitizeArgs(args: any[]): any[] {
    return args.map((arg) => {
      if (typeof arg === 'object') {
        try {
          // For context, only include small objects
          const stringified = JSON.stringify(arg);
          if (stringified.length > 200) {
            return { _truncated: true, type: typeof arg, size: stringified.length };
          }
          return arg;
        } catch {
          return { _error: 'Could not stringify object' };
        }
      }
      return arg;
    });
  }

  private interceptConsole() {
    // Intercept console.log
    console.log = (...args) => {
      this.originalConsole.log(...args);
      this.sendToServer({
        level: 'log',
        message: this.formatMessage(args),
        args: this.sanitizeArgs(args),
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: this.getDeviceInfo().userAgent,
        userId: this.userId,
        deviceInfo: this.getDeviceInfo(),
      });
    };

    // Intercept console.info
    console.info = (...args) => {
      this.originalConsole.info(...args);
      this.sendToServer({
        level: 'info',
        message: this.formatMessage(args),
        args: this.sanitizeArgs(args),
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: this.getDeviceInfo().userAgent,
        userId: this.userId,
        deviceInfo: this.getDeviceInfo(),
      });
    };

    // Intercept console.warn
    console.warn = (...args) => {
      this.originalConsole.warn(...args);
      this.sendToServer({
        level: 'warn',
        message: this.formatMessage(args),
        args: this.sanitizeArgs(args),
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: this.getDeviceInfo().userAgent,
        userId: this.userId,
        deviceInfo: this.getDeviceInfo(),
      });
    };

    // Intercept console.error
    console.error = (...args) => {
      this.originalConsole.error(...args);
      this.sendToServer({
        level: 'error',
        message: this.formatMessage(args),
        args: this.sanitizeArgs(args),
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: this.getDeviceInfo().userAgent,
        userId: this.userId,
        deviceInfo: this.getDeviceInfo(),
      });
    };

    // Intercept console.debug
    console.debug = (...args) => {
      this.originalConsole.debug(...args);
      this.sendToServer({
        level: 'debug',
        message: this.formatMessage(args),
        args: this.sanitizeArgs(args),
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: this.getDeviceInfo().userAgent,
        userId: this.userId,
        deviceInfo: this.getDeviceInfo(),
      });
    };
  }

  // Method to restore original console methods
  restore() {
    console.log = this.originalConsole.log;
    console.info = this.originalConsole.info;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.debug = this.originalConsole.debug;
  }
}

// Create a singleton instance lazily
// while rendering this caused a window is not defined error
let _consoleInterceptor: ConsoleInterceptor | null = null;

export const consoleInterceptor = {
  get instance(): ConsoleInterceptor {
    if (!_consoleInterceptor) {
      _consoleInterceptor = new ConsoleInterceptor();
    }
    return _consoleInterceptor;
  },

  setUserId(userId: string) {
    this.instance.setUserId(userId);
  },

  setEnabled(enabled: boolean) {
    this.instance.setEnabled(enabled);
  },

  setLogLevel(level: string) {
    this.instance.setLogLevel(level);
  },

  restore() {
    this.instance.restore();
  }
};

// Export for manual control
